import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Download, Plus, History, Trash2, Pencil, X, Minus } from "lucide-react";
import { toast } from "sonner";
import { api, friendlyError, API, getGuestToken } from "@/lib/api";
import { DISH_CATEGORIES, DIETARY_OPTIONS, STATUS_LABELS, formatPhone } from "@/lib/constants";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "attending", label: "Attending" },
  { id: "declined", label: "Declined" },
  { id: "cancelled", label: "No Longer Attending" },
  { id: "bringing", label: "Bringing Food" },
  { id: "notbringing", label: "Not Bringing" },
  { id: "dietary", label: "Dietary Restrictions" },
];

const badge = (s) => ({
  attending: "bg-[#2D6A4F]/10 text-[#2D6A4F]", declined: "bg-inkMuted/15 text-inkSoft", cancelled: "bg-[#D97706]/10 text-[#D97706]",
}[s]);

export function GuestsTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null); // {mode:'add'|'edit', rsvp}
  const [historyOf, setHistoryOf] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const { data: rsvps = [] } = useQuery({ queryKey: ["host-rsvps"], queryFn: async () => (await api.get("/host/rsvps")).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["host-rsvps"] }); qc.invalidateQueries({ queryKey: ["host-stats"] }); };

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return rsvps.filter((r) => {
      if (t && !(`${r.primary_name} ${r.phone_display} ${r.dish?.name || ""} ${(r.guests || []).join(" ")}`.toLowerCase().includes(t))) return false;
      if (filter === "attending") return r.status === "attending";
      if (filter === "declined") return r.status === "declined";
      if (filter === "cancelled") return r.status === "cancelled";
      if (filter === "bringing") return r.dish_active && r.dish;
      if (filter === "notbringing") return !(r.dish_active && r.dish);
      if (filter === "dietary") return (r.dietary || []).filter((d) => d !== "None").length > 0;
      return true;
    });
  }, [rsvps, q, filter]);

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/host/export`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "friendsgiving_guests.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const del = async () => {
    try { await api.delete(`/host/rsvps/${confirmDel.id}`); toast.success("Record removed."); setConfirmDel(null); refresh(); }
    catch (e) { toast.error(friendlyError(e)); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="font-serif text-3xl text-espresso">Guest List</h1><p className="text-inkSoft mt-1">{filtered.length} of {rsvps.length} records</p></div>
        <div className="flex gap-2">
          <button onClick={exportCsv} className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-hair bg-white text-espresso font-semibold hover:border-gold transition" data-testid="host-export"><Download size={16} /> Export</button>
          <button onClick={() => setModal({ mode: "add", rsvp: null })} className="flex items-center gap-2 px-4 py-2.5 rounded-full gold-gradient text-espressoDeep font-bold shadow-gold active:scale-95 transition" data-testid="host-add-guest"><Plus size={16} /> Add Guest</button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-inkMuted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, or dish…" data-testid="host-search"
            className="w-full bg-white border border-hair rounded-full pl-11 pr-4 py-3 text-espresso focus:border-gold outline-none" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`host-filter-${f.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap border transition ${filter === f.id ? "gold-gradient text-espressoDeep border-transparent" : "bg-white border-hair text-inkSoft hover:border-gold"}`}>{f.label}</button>
          ))}
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-hair shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="host-guest-table">
            <thead className="bg-cream2/60 text-left"><tr className="text-eyebrow text-inkMuted">
              <th className="px-5 py-4">Guest</th><th className="px-5 py-4">Phone</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Party</th><th className="px-5 py-4">Dish</th><th className="px-5 py-4">Dietary</th><th className="px-5 py-4">Updated</th><th className="px-5 py-4"></th>
            </tr></thead>
            <tbody className="divide-y divide-hair">
              {filtered.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-inkSoft">No guests match.</td></tr>}
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-cream2/40 transition" data-testid={`guest-row-${r.id}`}>
                  <td className="px-5 py-4"><p className="font-semibold text-espresso">{r.primary_name}</p>{r.guests?.length > 0 && <p className="text-xs text-inkMuted">+ {r.guests.join(", ")}</p>}</td>
                  <td className="px-5 py-4 text-inkSoft whitespace-nowrap">{r.phone_display}</td>
                  <td className="px-5 py-4"><span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge(r.status)}`}>{STATUS_LABELS[r.status]}</span></td>
                  <td className="px-5 py-4 text-espresso font-medium">{r.party_size}</td>
                  <td className="px-5 py-4 text-inkSoft">{r.dish_active && r.dish ? r.dish.name : "—"}</td>
                  <td className="px-5 py-4 text-inkSoft">{(r.dietary || []).filter((d) => d !== "None").join(", ") || "—"}</td>
                  <td className="px-5 py-4 text-inkMuted whitespace-nowrap">{new Date(r.updated_at).toLocaleDateString()}</td>
                  <td className="px-5 py-4"><div className="flex gap-1 justify-end">
                    <button onClick={() => setHistoryOf(r)} className="p-2 rounded-lg hover:bg-cream2 text-inkSoft" data-testid={`history-${r.id}`}><History size={16} /></button>
                    <button onClick={() => setModal({ mode: "edit", rsvp: r })} className="p-2 rounded-lg hover:bg-cream2 text-gold" data-testid={`edit-${r.id}`}><Pencil size={16} /></button>
                    <button onClick={() => setConfirmDel(r)} className="p-2 rounded-lg hover:bg-[#D97706]/10 text-[#D97706]" data-testid={`delete-${r.id}`}><Trash2 size={16} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && <GuestModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
      {historyOf && <HistoryModal rsvp={historyOf} onClose={() => setHistoryOf(null)} />}
      {confirmDel && (
        <Backdrop onClose={() => setConfirmDel(null)}>
          <div className="text-center" data-testid="delete-modal">
            <h3 className="font-serif text-2xl text-espresso">Remove this record?</h3>
            <p className="text-inkSoft mt-2">This permanently deletes {confirmDel.primary_name}'s RSVP. This can't be undone.</p>
            <div className="mt-6 space-y-3">
              <button onClick={del} className="w-full bg-[#D97706] text-white font-bold py-3.5 rounded-full active:scale-95" data-testid="delete-confirm">Yes, remove</button>
              <button onClick={() => setConfirmDel(null)} className="w-full py-3 text-inkSoft font-medium">Cancel</button>
            </div>
          </div>
        </Backdrop>
      )}
    </div>
  );
}

function Backdrop({ children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-espressoDeep/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-cream w-full ${wide ? "sm:max-w-lg" : "sm:max-w-sm"} rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 max-h-[92vh] overflow-y-auto`}>{children}</div>
    </div>
  );
}

function GuestModal({ modal, onClose, onSaved }) {
  const r = modal.rsvp;
  const [form, setForm] = useState({
    primary_name: r?.primary_name || "", phone: r?.phone_display || "", status: r?.status || "attending",
    party_size: r?.party_size || 1, guests: r?.guests || [], bringing_dish: r?.bringing_dish || false,
    dish: r?.dish || { category: "Main Dish", name: "" }, dietary: r?.dietary || [], dietary_notes: r?.dietary_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, guests: form.guests.slice(0, form.party_size - 1).filter((g) => g?.trim()), dish: form.bringing_dish && form.dish.name ? form.dish : null };
      if (modal.mode === "add") await api.post("/host/rsvps", { ...payload, sms_consent: true });
      else await api.put(`/host/rsvps/${r.id}`, payload);
      toast.success(modal.mode === "add" ? "Guest added." : "RSVP updated.");
      onSaved();
    } catch (e) { toast.error(friendlyError(e)); } finally { setSaving(false); }
  };

  return (
    <Backdrop onClose={onClose} wide>
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-serif text-2xl text-espresso">{modal.mode === "add" ? "Add Guest" : "Edit RSVP"}</h3>
        <button onClick={onClose} className="text-inkMuted hover:text-espresso"><X /></button>
      </div>
      <div className="space-y-4" data-testid="host-guest-modal">
        <input value={form.primary_name} onChange={(e) => set({ primary_name: e.target.value })} placeholder="Full name" className="w-full bg-white border border-hair rounded-xl px-4 py-3 focus:border-gold outline-none" data-testid="modal-name" />
        <input value={form.phone} onChange={(e) => set({ phone: formatPhone(e.target.value) })} placeholder="(561) 555-1234" disabled={modal.mode === "edit"} className="w-full bg-white border border-hair rounded-xl px-4 py-3 focus:border-gold outline-none disabled:opacity-60" data-testid="modal-phone" />
        <div className="flex gap-2">
          {["attending", "declined", "cancelled"].map((s) => (
            <button key={s} onClick={() => set({ status: s })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition ${form.status === s ? "gold-gradient text-espressoDeep border-transparent" : "bg-white border-hair text-inkSoft"}`} data-testid={`modal-status-${s}`}>{STATUS_LABELS[s]}</button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-eyebrow text-inkMuted">Party</span>
          <button onClick={() => set({ party_size: Math.max(1, form.party_size - 1) })} className="w-10 h-10 rounded-full bg-white border border-hair grid place-items-center" data-testid="modal-party-minus"><Minus size={18} /></button>
          <span className="font-serif text-2xl text-espresso w-8 text-center" data-testid="modal-party-count">{form.party_size}</span>
          <button onClick={() => set({ party_size: form.party_size + 1 })} className="w-10 h-10 rounded-full gold-gradient grid place-items-center" data-testid="modal-party-plus"><Plus size={18} /></button>
        </div>
        {form.party_size > 1 && Array.from({ length: form.party_size - 1 }).map((_, i) => (
          <input key={i} value={form.guests[i] || ""} placeholder={`Guest ${i + 2}`} onChange={(e) => { const g = [...form.guests]; g[i] = e.target.value; set({ guests: g }); }} className="w-full bg-white border border-hair rounded-xl px-4 py-3 focus:border-gold outline-none" data-testid={`modal-guest-${i}`} />
        ))}
        <div className="flex gap-2">
          <button onClick={() => set({ bringing_dish: true })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border ${form.bringing_dish ? "gold-gradient text-espressoDeep border-transparent" : "bg-white border-hair"}`} data-testid="modal-bringing-yes">Bringing dish</button>
          <button onClick={() => set({ bringing_dish: false })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border ${!form.bringing_dish ? "bg-espresso text-cream border-transparent" : "bg-white border-hair"}`} data-testid="modal-bringing-no">No dish</button>
        </div>
        {form.bringing_dish && (
          <div className="space-y-3">
            <select value={form.dish.category} onChange={(e) => set({ dish: { ...form.dish, category: e.target.value } })} className="w-full bg-white border border-hair rounded-xl px-4 py-3 focus:border-gold outline-none" data-testid="modal-dish-cat">
              {DISH_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
            </select>
            <input value={form.dish.name} onChange={(e) => set({ dish: { ...form.dish, name: e.target.value } })} placeholder="Dish name" className="w-full bg-white border border-hair rounded-xl px-4 py-3 focus:border-gold outline-none" data-testid="modal-dish-name" />
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {DIETARY_OPTIONS.map((d) => {
            const active = form.dietary.includes(d);
            return <button key={d} onClick={() => set({ dietary: d === "None" ? (active ? [] : ["None"]) : active ? form.dietary.filter((x) => x !== d) : [...form.dietary.filter((x) => x !== "None"), d] })} className={`px-3.5 py-2 rounded-full border text-sm ${active ? "gold-gradient text-espressoDeep border-transparent" : "bg-white border-hair"}`} data-testid={`modal-diet-${d.replace(/[^a-zA-Z]/g, "")}`}>{d}</button>;
          })}
        </div>
      </div>
      <button onClick={save} disabled={saving || !form.primary_name || !form.phone} className="w-full mt-6 gold-gradient text-espressoDeep font-bold py-3.5 rounded-full shadow-gold active:scale-95 transition disabled:opacity-40" data-testid="modal-save">{saving ? "Saving…" : "Save"}</button>
    </Backdrop>
  );
}

function HistoryModal({ rsvp, onClose }) {
  const { data: history = [] } = useQuery({ queryKey: ["history", rsvp.id], queryFn: async () => (await api.get(`/host/rsvps/${rsvp.id}/history`)).data });
  return (
    <Backdrop onClose={onClose} wide>
      <div className="flex items-center justify-between mb-5">
        <div><p className="text-eyebrow text-inkMuted">RSVP History</p><h3 className="font-serif text-2xl text-espresso">{rsvp.primary_name}</h3></div>
        <button onClick={onClose} className="text-inkMuted hover:text-espresso"><X /></button>
      </div>
      <div className="relative pl-6" data-testid="history-timeline">
        <div className="absolute left-1.5 top-2 bottom-2 w-px bg-hair" />
        {history.map((h, i) => (
          <div key={i} className="relative pb-6 last:pb-0">
            <div className="absolute -left-[18px] top-1 w-3 h-3 rounded-full gold-gradient" />
            <p className="text-eyebrow text-inkMuted">{new Date(h.timestamp).toLocaleString()}</p>
            <p className="text-espresso font-medium mt-0.5">{h.change_type}</p>
            {h.new && <p className="text-sm text-inkSoft">{h.previous ? `${h.previous} → ${h.new}` : h.new}</p>}
          </div>
        ))}
      </div>
    </Backdrop>
  );
}
